import { Dialog, DialogFooter, ITheme, Panel, PrimaryButton, Stack, Text } from '@fluentui/react';
import { CampusDto } from 'services/client-api-wrapper/campus_api/responses.dto';
import { OrgBrandingDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import { IC } from 'components/SVGIcon';
import TrialPeriodAboutToEndModal from 'components/TrialPeriodAboutToEndModal/TrialPeriodAboutToEndModal';
import AreaList from 'features/Areas/AreaList/AreaList';
import ChangeLog from 'features/ChangeLog/ChangeLog';
import VisitorCard from 'features/Header/children/InvitationsManager/children/VisitorsCard/VisitorsCard';
import InvitationsManager from 'features/Header/children/InvitationsManager/InvitationsManager';
import Members from 'features/Members/Members';
import { aNotification } from 'features/NotificationsLayer/hooks/atoms';
import NotificationsLayer from 'features/NotificationsLayer/NotificationsLayer';
import { Menu } from 'kits/IconKit2';
import IvicosStrings from 'kits/language/stringKit';
import React, { useState } from 'react';
import { SetterOrUpdater, useRecoilState } from 'recoil';
import { useVisitorList } from 'shared-state/presence/hooks';
import AreasPanel from 'features/Areas/AreaPanel/AreaPanel';
import CampusSettings from 'features/CampusSettings/CampusSettings';
import { IvicosColors } from 'kits/colorKit';
import LobbyRedirectModal from './LobbyRedirectModal';

export interface IDashboardViewProps {
    selectedKey: string;
    theme: ITheme;
    campus?: CampusDto;
    isOpen: boolean;
    openPanel: () => void;
    dismissPanel: () => void;
    tabIsVisible: {
        areas: boolean;
        members: boolean;
        settings: boolean;
        billing: boolean;
        log: boolean;
    };
    setTabIsVisible: React.Dispatch<
        React.SetStateAction<{
            areas: boolean;
            members: boolean;
            settings: boolean;
            billing: boolean;
            log: boolean;
        }>
    >;
    isAdmin?: boolean;
    branding?: OrgBrandingDto;
    pivotDisplaySmallDevice: string;
    burgerMenu: string;
    campusNameSmallDevice: string;
    stackTitleSmallDevice: string;
    smallDevice: boolean;
    invitationsManagerModal: boolean;
    setInvitationsManagerModal: SetterOrUpdater<boolean>;
    trialExpiringWarningDisplayedToday: boolean;
    isOrgInTrial: string;
    identityClaims: any;
    hideDashboardTabs: boolean;
}

const DashboardMobileView: React.FC<IDashboardViewProps> = (props) => {
    const {
        theme,
        campus,
        isOpen,
        openPanel,
        dismissPanel,
        tabIsVisible,
        setTabIsVisible,
        isAdmin,
        branding,
        burgerMenu,
        campusNameSmallDevice,
        invitationsManagerModal,
        setInvitationsManagerModal,
        trialExpiringWarningDisplayedToday,
        isOrgInTrial,
        hideDashboardTabs
    } = props;

    const visitors = useVisitorList()[0];

    const [hideDialog, setHideDialog] = useRecoilState<any>(aNotification);

    const [members] = useState<any>([]);

    return (
        <Stack style={{ height: 'calc(100% - 48px)' }}>
            <LobbyRedirectModal themeColor={theme?.palette?.themePrimary} />
            <AreasPanel />
            <NotificationsLayer />
            {invitationsManagerModal && <InvitationsManager setInvitationsManagerModal={setInvitationsManagerModal} />}
            {isAdmin && !trialExpiringWarningDisplayedToday && isOrgInTrial && <TrialPeriodAboutToEndModal />}

            <Panel isOpen={isOpen} onDismiss={dismissPanel} closeButtonAriaLabel={IvicosStrings.closeActionTitle}>
                <img src={branding && branding.orgLogo} style={{ width: '50%' }} />

                <Stack style={{ marginTop: '15px' }}>
                    <Stack>
                        <Text
                            variant="xLarge"
                            onClick={() => {
                                dismissPanel();
                                setTabIsVisible({ areas: true, members: false, settings: false, billing: false, log: false });
                            }}
                            style={
                                tabIsVisible.areas
                                    ? {
                                          fontSize: '27px',
                                          color: IvicosColors.Mittelblau,
                                          marginBottom: '35px',
                                          fontWeight: 'bolder'
                                      }
                                    : { marginBottom: '30px' }
                            }
                        >
                            {IvicosStrings.availableAreasTitle}
                        </Text>
                    </Stack>
                    {isAdmin && (
                        <Text
                            variant="xLarge"
                            onClick={() => {
                                dismissPanel();
                                setTabIsVisible({ areas: false, members: true, settings: false, billing: false, log: false });
                            }}
                            style={
                                tabIsVisible.members
                                    ? {
                                          fontSize: '27px',
                                          color: IvicosColors.Mittelblau,
                                          marginBottom: '35px',
                                          fontWeight: 'bolder'
                                      }
                                    : { marginBottom: '30px' }
                            }
                        >
                            {IvicosStrings.manageAccessTitle}
                        </Text>
                    )}
                    {isAdmin && (
                        <Text
                            variant="xLarge"
                            onClick={() => {
                                dismissPanel();
                                setTabIsVisible({ areas: false, members: false, settings: true, billing: false, log: false });
                            }}
                            style={
                                tabIsVisible.settings
                                    ? {
                                          fontSize: '27px',
                                          color: IvicosColors.Mittelblau,
                                          marginBottom: '35px',
                                          fontWeight: 'bolder'
                                      }
                                    : { marginBottom: '30px' }
                            }
                        >
                            {IvicosStrings.campusSettingsTitle}
                        </Text>
                    )}
                    {isAdmin && (
                        <Text
                            variant="xLarge"
                            onClick={() => {
                                dismissPanel();
                                setTabIsVisible({ areas: false, members: false, settings: false, billing: true, log: false });
                            }}
                            style={
                                tabIsVisible.billing
                                    ? {
                                          fontSize: '27px',
                                          color: IvicosColors.Mittelblau,
                                          marginBottom: '35px',
                                          fontWeight: 'bolder'
                                      }
                                    : { marginBottom: '30px' }
                            }
                        >
                            {IvicosStrings.billingTitle}
                        </Text>
                    )}
                    <Text
                        variant="xLarge"
                        onClick={() => {
                            dismissPanel();
                            setTabIsVisible({ areas: false, members: false, settings: false, billing: false, log: true });
                        }}
                        style={
                            tabIsVisible.log
                                ? {
                                      fontSize: '27px',
                                      color: '#0078D4',
                                      marginBottom: '35px',
                                      fontWeight: 'bolder'
                                  }
                                : { marginBottom: '30px' }
                        }
                    >
                        {IvicosStrings.changeLogTitle}
                    </Text>
                </Stack>
            </Panel>

            <Stack verticalAlign="stretch" style={{ maxWidth: '100%', paddingTop: 16, backgroundColor: theme.palette.neutralLight, height: '100%' }}>
                <Stack horizontal style={{ padding: '0px 31px', position: 'relative', marginBottom: '16px' }}>
                    {!hideDashboardTabs && (
                        <Stack style={{ position: 'absolute' }}>
                            <IC size={32} variant="custom">
                                <Menu className={burgerMenu} onClick={openPanel} />
                            </IC>
                        </Stack>
                    )}

                    <Stack style={{ width: '100%' }}>
                        <Text
                            aria-label="campus-name-title"
                            className={campusNameSmallDevice}
                            style={{ color: theme.semanticColors.bodyText }}
                            variant="xxLarge"
                        >
                            {campus?.name || ''}
                        </Text>
                    </Stack>
                </Stack>

                <Stack style={{ height: '100%' }}>
                    {tabIsVisible.areas && (
                        <Stack grow>
                            <AreaList isAreaGroupRestricted={true} />
                        </Stack>
                    )}

                    {tabIsVisible.members && <Members />}

                    {tabIsVisible.settings && <CampusSettings />}

                    {tabIsVisible.log && (
                        <Stack>
                            <ChangeLog />
                        </Stack>
                    )}
                </Stack>

                <Dialog hidden={hideDialog}>
                    {visitors.length > 0 &&
                        visitors.map((visitor: any) => (
                            <VisitorCard
                                name={visitor.displayName}
                                key={visitor.uid}
                                host={visitor.host}
                                members={members}
                                invitationId={visitor.invitationId}
                                subInfo={visitor.subInfo}
                            />
                        ))}

                    <DialogFooter>
                        <PrimaryButton onClick={() => setHideDialog(!hideDialog)} text={IvicosStrings.closeActionTitle} />
                    </DialogFooter>
                </Dialog>
            </Stack>
        </Stack>
    );
};

export default DashboardMobileView;
